<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <HyperspeedBackground
      class="absolute inset-0 z-0"
      :beam-count="120"
      color-scheme="rainbow"
      :speed-factor="1.5"
    />
    <div
      class="max-w-xl w-full space-y-6 bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10 text-center"
    >
      <ShineBorder
        :border-width="2"
        :duration="24"
        :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']"
      />
      <div class="relative flex items-center justify-center">
        <LogoMainLink class="z-20" />
      </div>

      <div v-if="status === 'confirmed'" class="space-y-4">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <CheckCircle class="h-6 w-6 text-green-600" />
        </div>
        <h1 class="text-2xl text-gray-900 font-light">Thank you!</h1>
        <p class="text-gray-600">You confirmed your referral submission for:</p>
        <div v-if="applications.length" class="mt-4 p-4 rounded-lg bg-gray-50 text-left space-y-2">
          <ul class="space-y-1 text-sm text-gray-600">
            <li
              v-for="app in applications"
              :key="app.id"
              class="flex flex-wrap items-baseline gap-x-1"
            >
              <span class="font-medium text-gray-900">
                {{ app.applicant?.full_name || app.applicant_name || 'Applicant' }}
              </span>
              <a
                :href="`https://outtalent.com/profile/${app.applicant?.username}`"
                target="_blank"
                v-if="app.applicant?.username"
                class="text-gray-500"
                >(@{{ app.applicant.username }})</a
              >
              <span class="text-gray-500">–</span>
              <span class="font-medium text-gray-900">
                {{ app.job_position?.title || app.role_name }}
              </span>
              <span class="text-gray-500">at</span>
              <span class="font-medium text-gray-900">
                {{ app.job_position?.company || app.employer_name }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div v-else-if="status === 'declined'" class="space-y-4">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <XCircle class="h-6 w-6 text-gray-500" />
        </div>
        <h1 class="text-2xl text-gray-900 font-light">Referral Declined</h1>
        <p class="text-gray-600">You declined making a referral for:</p>
        <div v-if="applications.length" class="mt-4 p-4 rounded-lg bg-gray-50 text-left space-y-2">
          <ul class="space-y-1 text-sm text-gray-600">
            <li
              v-for="app in applications"
              :key="app.id"
              class="flex flex-wrap items-baseline gap-x-1"
            >
              <span class="font-medium text-gray-900">
                {{ app.applicant?.full_name || app.applicant_name || 'Applicant' }}
              </span>
              <span v-if="app.applicant?.username" class="text-gray-500"
                >(@{{ app.applicant.username }})</span
              >
              <span class="text-gray-500">–</span>
              <span class="font-medium text-gray-900">
                {{ app.job_position?.title || app.role_name }}
              </span>
              <span class="text-gray-500">at</span>
              <span class="font-medium text-gray-900">
                {{ app.job_position?.company || app.employer_name }}
              </span>
            </li>
          </ul>
        </div>
        <p class="text-gray-600">If possible, please let us know the reason for declining or any feedback you might have. Thank you for your consideration.</p>
      </div>

      <div v-else class="space-y-4">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <AlertCircle class="h-6 w-6 text-red-600" />
        </div>
        <h1 class="text-2xl text-gray-900 font-light">Something went wrong</h1>
        <p class="text-gray-600">{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { CheckCircle, XCircle, AlertCircle } from 'lucide-vue-next'
import HyperspeedBackground from '@/components/layout/HyperspeedBackground.vue'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'

const SESSION_KEY = 'referralActionResult'

const route = useRoute()

const status = computed(() => route.query.status as string)
const errorMessage = computed(
  () => (route.query.message as string) || 'An unexpected error occurred.'
)

const applications = ref<
  Array<{
    id: number
    applicant?: { full_name: string; username: string }
    job_position?: { title: string; company: string }
    applicant_name?: string
    role_name?: string
    employer_name?: string
  }>
>([])

onMounted(() => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      if (Array.isArray(data.applications)) {
        applications.value = data.applications
      }
      sessionStorage.removeItem(SESSION_KEY)
    }
  } catch {
    // Ignore parse errors
  }
})
</script>
